import React, { useContext, useEffect, useState } from "react";
import { Modal, Input, Row, Col, Select, Radio, Segmented } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import DirectoryContext from "../../../context/directoryManagementContext";
import useAxiosInstance from "../../../lib/useAxiosInstance";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import ErrorMessage from "../../Forms/ErrorMessage";
import Places from "../../../Pages/gms/Places";
import { useTranslation } from "react-i18next";

const AddContactModal = ({ isOpen, isAddMode }) => {
  const directoryContext = useContext(DirectoryContext);
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  // const [activeType, setActiveType] = useState("party"); // "party" or "personal"
  const [activeType, setActiveType] = useState(
  isAddMode ? "party" : directoryContext.editType || "party"
);
  const [isOtherPosition, setIsOtherPosition] = useState(false);
  const [locationData, setLocationData] = useState({});
  const user = JSON.parse(localStorage.getItem("user"));

  // fetch roles only if Party Member selected
  const fetchPositions = async () => axiosInstance.get("api/clients/getclientroles");
  const { data: positionsData } = useQuery("positions", fetchPositions, {
    enabled: activeType === "party",
  });

  // Formik
  const formik = useFormik({
    initialValues: {
      firstName: isAddMode ? "" : directoryContext.editData?.firstName || "",
      lastName: isAddMode ? "" : directoryContext.editData?.lastName || "",
      position: isAddMode ? "" : directoryContext.editData?.position || "",
      customPosition: isAddMode ? "" : directoryContext.editData?.customPosition || "",
      group: isAddMode ? "" : directoryContext.editData?.group || "",
      phone: isAddMode ? "" : directoryContext.editData?.phone || "",
      email: isAddMode ? "" : directoryContext.editData?.email || "",
      address: isAddMode ? "" : directoryContext.editData?.address || "",
      place: isAddMode ? "" : directoryContext.editData?.place || "",
      coordinates: isAddMode
        ? { lat: "", lng: "" }
        : {
            lat: directoryContext.editData?.coordinates?.lat || "",
            lng: directoryContext.editData?.coordinates?.lng || "",
          },
      dob: isAddMode ? "" : directoryContext.editData?.dob?.split("T")[0] || "",
      isPriority: isAddMode ? false : directoryContext.editData?.isPriority || false,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      phone: Yup.string().required("Phone is required"),
    }),
    onSubmit: (values) => {
      const payload = { ...values };

      // handle Party Member specific
      if (activeType === "party" && values.position === "Other") {
        payload.position = values.customPosition;
      }

      payload.place = locationData?.address || values.place;
      payload.coordinates.lat = locationData?.lat || values.coordinates.lat;
      payload.coordinates.lng = locationData?.lng || values.coordinates.lng;

      if (isAddMode) {
        createContact.mutate(payload);
      } else {
        updateContact.mutate(payload);
      }
    },
  });

  // Mutations
  const createContact = useMutation(
    (data) =>
      activeType === "party"
        ? axiosInstance.post("/api/directory/party-members", data)
        : axiosInstance.post("/api/directory/other-contacts", data),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(activeType === "party" ? "party-members" : "other-contacts");
        toast.success(res.data.message);
        closeModal();
      },
      onError: (err) => toast.error(err?.response?.data?.message || "Error"),
    }
  );

  const updateContact = useMutation(
    (data) =>
      activeType === "party"
        ? axiosInstance.put(`/api/directory/party-members/${directoryContext.editData._id}`, data)
        : axiosInstance.put(`/api/directory/other-contacts/${directoryContext.editData._id}`, data),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(activeType === "party" ? "party-members" : "other-contacts");
        toast.success(res.data.message);
        closeModal();
      },
      onError: (err) => toast.error(err?.response?.data?.message || "Error"),
    }
  );

  useEffect(() => {
  if (!isAddMode && directoryContext.editType) {
    setActiveType(directoryContext.editType);
  }
}, [directoryContext.editType]);

  useEffect(() => {
    if (locationData?.address) {
      formik.setFieldValue("place", locationData.address);
    }
    if (locationData?.lat && locationData?.lng) {
      formik.setFieldValue("coordinates", {
        lat: locationData.lat,
        lng: locationData.lng,
      });
    }
  }, [locationData]);

  const closeModal = () => {
    directoryContext.updateOpenModal(false);
    directoryContext.updateAddMode(true);
    setLocationData({})
    formik.resetForm();
  };

  return (
    <Modal
      className="user-modal-wrapper !w-[70%] top-[40px]"
      open={isOpen}
      onCancel={closeModal}
      title={isAddMode ? "Add New Contact" : "Edit Contact"}
      onOk={formik.handleSubmit}
      okText={isAddMode ? "Add" : "Save"}
      destroyOnClose
    >
      {/* Toggle */}
      <Segmented
        block
        value={activeType}
        onChange={setActiveType}
        options={[
          { label: "Party Member", value: "party" },
          { label: "Personal Contact", value: "personal" },
        ]}
      />

      <form className="mt-6" onSubmit={formik.handleSubmit}>
        <Row gutter={16}>
          <Col span={12}>
            <label>First Name</label>
            <Input name="firstName" value={formik.values.firstName} onChange={formik.handleChange} />
            <ErrorMessage hasError={!!formik.errors.firstName} message={formik.errors.firstName} />
          </Col>
          <Col span={12}>
            <label>Last Name</label>
            <Input name="lastName" value={formik.values.lastName} onChange={formik.handleChange} />
            <ErrorMessage hasError={!!formik.errors.lastName} message={formik.errors.lastName} />
          </Col>
        </Row>

        {/* Party Member fields */}
        {activeType === "party" && (
          <Row gutter={16} className="mt-3">
            <Col span={12}>
              <label>Position</label>
              <Select
                value={formik.values.position}
                onChange={(value) => {
                  if (value === "Other") {
                    setIsOtherPosition(true);
                    formik.setFieldValue("position", "Other");
                    formik.setFieldValue("group", "common");
                  } else {
                    setIsOtherPosition(false);
                    formik.setFieldValue("position", value);
                  }
                }}
                placeholder="Select Position"
                className="w-full"
              >
                {positionsData?.data?.data?.map((pos) => (
                  <Select.Option key={pos._id} value={pos.name}>
                    {pos.name}
                  </Select.Option>
                ))}
                <Select.Option value="Other">Other</Select.Option>
              </Select>
              <ErrorMessage hasError={!!formik.errors.position} message={formik.errors.position} />
            </Col>

            {isOtherPosition && (
              <Col span={12}>
                <label>Custom Position</label>
                <Input
                  name="customPosition"
                  value={formik.values.customPosition}
                  onChange={formik.handleChange}
                />
                <ErrorMessage hasError={!!formik.errors.customPosition} message={formik.errors.customPosition} />
              </Col>
            )}
          </Row>
        )}

        {/* Common fields */}
        <Row gutter={16} className="mt-3">
          <Col span={12}>
            <label>Phone</label>
            <Input name="phone" value={formik.values.phone} onChange={formik.handleChange} />
            <ErrorMessage hasError={!!formik.errors.phone} message={formik.errors.phone} />
          </Col>
          <Col span={12}>
            <label>Email</label>
            <Input name="email" value={formik.values.email} onChange={formik.handleChange} />
            <ErrorMessage hasError={!!formik.errors.email} message={formik.errors.email} />
          </Col>
        </Row>

        <Row gutter={16} className="mt-3">
          <Col span={24}>
            <label>Address</label>
            <Input name="address" value={formik.values.address} onChange={formik.handleChange} />
            <ErrorMessage hasError={!!formik.errors.address} message={formik.errors.address} />
          </Col>
        </Row>

        <Row gutter={16} className="mt-3">
          <Col span={12}>
            <label>Date of Birth</label>
            <Input type="date" name="dob" value={formik.values.dob} onChange={formik.handleChange} />
            <ErrorMessage hasError={!!formik.errors.dob} message={formik.errors.dob} />
          </Col>

          {(user?.role === "client" || user?.role === "chief_of_staff") && formik.values.dob && (
            <Col span={12}>
              <label>Priority</label>
              <Radio.Group
                name="isPriority"
                value={formik.values.isPriority}
                onChange={(e) => formik.setFieldValue("isPriority", e.target.value)}
              >
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
              <ErrorMessage hasError={!!formik.errors.isPriority} message={formik.errors.isPriority} />
            </Col>
          )}
        </Row>

        {/* Location */}
        <Row className="mt-3" gutter={16}>
          <Col span={24}>
            Location <span className="required-field">*</span>
            <Places
              setLocationData={setLocationData}
              initialLocation={{
                lat: formik.values.coordinates.lat,
                lng: formik.values.coordinates.lng,
                address: formik.values.place,
              }}
            />
          </Col>
        </Row>
      </form>
    </Modal>
  );
};

export default AddContactModal;
